import type { TypeCurveHeader } from 'types/api-responses';
import type {
  TcWellsQueryPayload,
  TcMapFilterMode,
  TcWellFilters,
  MapBounds,
} from './types';

interface BuildParams {
  typeCurveHeader: TypeCurveHeader;
  dcaScenarioId?: number | null;
  filters: TcWellFilters;
  mapFilterMode: TcMapFilterMode;
  mapBounds: MapBounds | null;
  aoiPolygon: GeoJSON.Polygon | null;
  page?: number;
  pageSize?: number;
  limit?: number;
  sortAttribute?: string;
  sortDirection?: string;
  text?: string;
}

export function buildTcWellsQueryPayload({
  typeCurveHeader,
  dcaScenarioId,
  filters,
  mapFilterMode,
  mapBounds,
  aoiPolygon,
  page,
  pageSize,
  limit,
  sortAttribute,
  sortDirection,
  text,
}: BuildParams): TcWellsQueryPayload {
  const payload: TcWellsQueryPayload = {
    page: page ?? 1,
    pageSize: pageSize ?? 100,
    limit: limit ?? 100,
    ...(sortAttribute && { sortAttribute }),
    ...(sortDirection && { sortDirection }),
    useElastic: true,
    includeTypeCurveFormation: true,
    structureGridCollectionId:
      typeCurveHeader.structureGridCollectionId ?? undefined,

    // Analog wells: the set of wells in the latest promoted type curve header.
    analogWellUwiIds: typeCurveHeader.analogWells ?? [],

    // Default filters from header
    holeDirectionIds: typeCurveHeader.developmentType
      ? [typeCurveHeader.developmentType.toUpperCase()]
      : undefined,

    // Type curve context (always sent)
    tcMode: true,
    typeCurveDateCreated: typeCurveHeader.createdAt ?? undefined,
    tcLateralLengthFt: typeCurveHeader.lateralLengthFt ?? undefined,
    useTcScenarioScaling:
      import.meta.env.VITE_TYPE_CURVE_SCENARIO_SCALING_ENABLED === 'true',
    typeCurveHeaderDetailsId: typeCurveHeader.id ?? undefined,
  };

  // Search text
  if (text) {
    payload.text = text;
  }

  // DCA scenario
  if (dcaScenarioId) {
    payload.dcaScenarioId = dcaScenarioId;
  }

  // Spatial filter based on map mode
  switch (mapFilterMode) {
    case 'type-curve-area':
      if (typeCurveHeader.geometry) {
        payload.polygonEwkb = typeCurveHeader.geometry;
      }
      break;
    case 'visible-extent':
      if (mapBounds) {
        payload.bounding_box = {
          sw_lng: mapBounds.sw_lng,
          sw_lat: mapBounds.sw_lat,
          ne_lng: mapBounds.ne_lng,
          ne_lat: mapBounds.ne_lat,
        };
      }
      break;
    case 'aoi-polygon':
      if (aoiPolygon) {
        payload.polygonGeojson = {
          coordinates: aoiPolygon.coordinates as number[][][],
          type: aoiPolygon.type,
        };
      }
      break;
  }

  // Map user filters to payload params
  applyUserFilters(payload, filters);

  return payload;
}

// Picklist filters -> Ids arrays. Shared by the live payload builder and the
// saved-filter definition round-trip (build + parse) so there is one source of
// truth for the filter-key ↔ payload-key mapping.
export const PICKLIST_FILTER_MAP: Record<string, keyof TcWellsQueryPayload> = {
  holeDirection: 'holeDirectionIds',
  status: 'statusIds',
  currentOperator: 'currentOperatorIds',
  uwi: 'uwiIds',
  leaseName: 'leaseNameIds',
  wellNumber: 'wellNumberIds',
  wellName: 'nameIds',
  proppantMeshSize: 'proppantMeshSizeIds',
  fluidSystem: 'fluidSystemIds',
  state: 'stateAbbreviationIds',
  wellQualifier: 'wellQualifierIds',
  landingZone: 'landingZoneIds',
  formation: 'formationIds',
  parentChildStatus: 'parentChildStatusIds',
  businessId: 'businessIds',
};

// Date range filters. Read from `min_<filterKey>` / `max_<filterKey>`.
export const DATE_RANGE_FILTER_MAP: Record<string, keyof TcWellsQueryPayload> =
  {
    permitDate: 'permitDate',
    spudDate: 'spudDate',
    completionDate: 'completionDate',
    firstProductionDate: 'firstProductionDate',
    jobDate: 'jobDate',
  };

// Maps the flat `TcWellFilters` record into the positively-set payload
// fragment (only keys the user actually set). Used to build a saved filter's
// `definition` and, via applyUserFilters, the live wells query.
export function mapUserFiltersToFragment(
  filters: TcWellFilters
): Partial<TcWellsQueryPayload> {
  const fragment: Partial<TcWellsQueryPayload> = {};

  for (const [filterKey, payloadKey] of Object.entries(PICKLIST_FILTER_MAP)) {
    const value = filters[filterKey];
    if (Array.isArray(value) && value.length > 0) {
      (fragment as any)[payloadKey] = value;
    }
  }

  for (const [filterKey, payloadKey] of Object.entries(DATE_RANGE_FILTER_MAP)) {
    const minVal = filters[`min_${filterKey}`] as string | undefined;
    const maxVal = filters[`max_${filterKey}`] as string | undefined;
    if (minVal || maxVal) {
      (fragment as any)[payloadKey] = {
        ...(minVal ? { start: minVal } : {}),
        ...(maxVal ? { end: maxVal } : {}),
      };
    }
  }

  for (const [filterKey, payloadKey] of Object.entries(
    NUMERIC_RANGE_FILTER_MAP
  )) {
    const minVal = filters[`min_${filterKey}`] as number | undefined;
    const maxVal = filters[`max_${filterKey}`] as number | undefined;
    if (minVal !== undefined || maxVal !== undefined) {
      (fragment as any)[payloadKey] = {
        ...(minVal !== undefined ? { min: minVal } : {}),
        ...(maxVal !== undefined ? { max: maxVal } : {}),
      };
    }
  }

  return fragment;
}

function applyUserFilters(
  payload: TcWellsQueryPayload,
  filters: TcWellFilters
): void {
  Object.assign(payload, mapUserFiltersToFragment(filters));

  // A picklist key present in `filters` but empty means the user explicitly
  // cleared it — remove the header default from the live payload.
  for (const [filterKey, payloadKey] of Object.entries(PICKLIST_FILTER_MAP)) {
    const value = filters[filterKey];
    const isSet = Array.isArray(value) && value.length > 0;
    if (!isSet && filterKey in filters) {
      delete (payload as any)[payloadKey];
    }
  }
}

// Numeric range filters. Read from `min_<filterKey>` / `max_<filterKey>`.
export const NUMERIC_RANGE_FILTER_MAP: Record<
  string,
  keyof TcWellsQueryPayload
> = {
  trueVerticalDepthFeet: 'trueVerticalDepth',
  perforatedLateralLengthFt: 'perforatedLateralLengthFt',
  eur_oil: 'oilEurBbl',
  eur_gas: 'gasEurMcf',
  eur_water: 'waterEurBbl',
  scaledOilEurBbl: 'scaledOilEurBbl',
  scaledGasEurMcf: 'scaledGasEurMcf',
  scaledWaterEurBbl: 'scaledWaterEurBbl',
  oilEurPerFtBblFt: 'oilEurPerFtBblFt',
  gasEurPerFtMcfFt: 'gasEurPerFtMcfFt',
  waterEurPerFtBblFt: 'waterEurPerFtBblFt',
  gorMcfBbl: 'gorMcfBbl',
  worBblBbl: 'worBblBbl',
  cgrBblMmcf: 'cgrBblMmcf',
  wgrBblMmcf: 'wgrBblMmcf',
  watercutPct: 'watercutPct',
  proppantLbsPerFt: 'proppantLbsPerFt',
  fluidGalPerFt: 'fluidGalPerFt',
  fluidBblPerFt: 'fluidBblPerFt',
  waterGalPerFt: 'waterGalPerFt',
  waterBblPerFt: 'waterBblPerFt',
  acidGalPerFt: 'acidGalPerFt',
  acidBblPerFt: 'acidBblPerFt',
  stageSpacingFt: 'stageSpacingFt',
  // Landing Analysis
  distanceToTopFt: 'distanceToTopFt',
  // Well Spacing — in zone
  closestInZoneTrueDistanceFt: 'closestInZoneTrueDistanceFt',
  closestInZoneHzDistanceFt: 'closestInZoneHzDistanceFt',
  closestInZoneVtDistanceFt: 'closestInZoneVtDistanceFt',
  closestInZoneOverlapPct: 'closestInZoneOverlapPct',
  secondClosestInZoneTrueDistanceFt: 'secondClosestInZoneTrueDistanceFt',
  secondClosestInZoneHzDistanceFt: 'secondClosestInZoneHzDistanceFt',
  secondClosestInZoneVtDistanceFt: 'secondClosestInZoneVtDistanceFt',
  secondClosestInZoneOverlapPct: 'secondClosestInZoneOverlapPct',
  avgInZoneTrueDistanceFt: 'avgInZoneTrueDistanceFt',
  avgInZoneHzDistanceFt: 'avgInZoneHzDistanceFt',
  avgInZoneVtDistanceFt: 'avgInZoneVtDistanceFt',
  // Well Spacing — any zone
  closestAnyZoneTrueDistanceFt: 'closestAnyZoneTrueDistanceFt',
  closestAnyZoneHzDistanceFt: 'closestAnyZoneHzDistanceFt',
  closestAnyZoneVtDistanceFt: 'closestAnyZoneVtDistanceFt',
  closestAnyZoneOverlapPct: 'closestAnyZoneOverlapPct',
  secondClosestAnyZoneTrueDistanceFt: 'secondClosestAnyZoneTrueDistanceFt',
  secondClosestAnyZoneHzDistanceFt: 'secondClosestAnyZoneHzDistanceFt',
  secondClosestAnyZoneVtDistanceFt: 'secondClosestAnyZoneVtDistanceFt',
  secondClosestAnyZoneOverlapPct: 'secondClosestAnyZoneOverlapPct',
  avgAnyZoneTrueDistanceFt: 'avgAnyZoneTrueDistanceFt',
  avgAnyZoneHzDistanceFt: 'avgAnyZoneHzDistanceFt',
  avgAnyZoneVtDistanceFt: 'avgAnyZoneVtDistanceFt',
  // Parent/child counts
  numberOfParents: 'numberOfParents',
  numberOfChildren: 'numberOfChildren',
  numberOfSiblings: 'numberOfSiblings',
};
